import { useState, useEffect, useMemo } from 'react';
import uuid from 'react-uuid';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import { db, timestamp } from '../../../firebase/config';
import { client } from '../../../hooks/Client';
import useSettings from '../../../hooks/Settings';
import useFocusHighlight from '../../../hooks/useFocusHighlight';
import CommonModal from '../../common/Modal';
import PremiumIcon from '../../common/PremiumIcon.jsx';
import SelectableGrid from '../../common/SelectableGrid.jsx';
import EffectDatabase from '../../meetstandaard/EffectDatabase';
import EffectDatabaseItemsList from '../../meetstandaard/EffectDatabaseItemsList';
import pickDefaultEffectsSector from '../../../helpers/pickDefaultEffectsSector';
import buildSectorTabs from '../../../helpers/meetstandaard/buildSectorTabs';
import buildMatrixRowPayload from '../../../helpers/meetstandaard/buildMatrixRowPayload';
import PortfolioChooserOption from '../subscribe/PortfolioChooserOption.jsx';
import SubscribePortfolioEffectsModal from '../subscribe/SubscribePortfolioEffectsModal.jsx';
import useParentPortfolios from '../../../hooks/useParentPortfolios';
import MeetstandardIcon from '../../../images/meetstandaard-icon.png';
import spinner from '../../../images/spinner-ripple.svg';
import clickableRowProps from '../../../helpers/a11y/clickableRowProps';

/**
 * Add-chooser for Effecten: pick a source (Meetstandaard, portfolio subscribe
 * per parent, or "eigen" — premium-gated) in an options modal, then run that
 * source's flow. Owns the Meetstandaard picker (sector tabs, targetgroup
 * validation, cascade write to QuestionnaireFields/MatrixQuestionRows/Columns).
 * Used by the standalone Effecten page (EffectManager) and the TOC navigator.
 * AI-CONTEXT: See docs/features/toc-navigator-add-chooser.md
 * AI-CONTEXT: See docs/patterns.md#ui-add-chooser-flow
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the chooser modal is open
 * @param {(open: boolean) => void} props.setOpen - Setter to close the chooser modal
 * @param {() => void} props.onOwnAdd - Called when the user picks "Eigen effect toevoegen" (not called on free plans)
 * @param {(added: Array<{id: string, docid: string}>) => void} [props.onAdded] - Called with the locally created effect(s) after a Meetstandaard save or portfolio subscribe
 * @param {number} props.effectsLength - Current number of effects (Position seed for new docs)
 */
const AddEffectFlow = ({ open, setOpen, onOwnAdd, onAdded, effectsLength }) => {
    // State
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEffects, setSelectedEffects] = useState([]);
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
    const [subscribePortfolioId, setSubscribePortfolioId] = useState('');

    // Sector filter state (Meetstandaard modal)
    const [selectedSector, setSelectedSector] = useState('all');
    const [sectorSeededFromSettings, setSectorSeededFromSettings] = useState(false);

    // Modal validation error
    const [modalError, setModalError] = useState('');

    // ✅ for scroll-to-missing (controls a CSS pulse highlight)
    const [scrollFocusId, setScrollFocusId] = useState('');

    // Hooks
    const {
        TertairyColor: color,
        subscription,
        selectedSectors: selectedSectorsFromOnboarding,
        loaded: settingsLoaded,
    } = useSettings();

    const effectDatabase = EffectDatabase();
    const { parentPortfolioIds, portfolioNameById } = useParentPortfolios();

    useEffect(() => {
        if (modalOpen) {
            setModalError('');
            setScrollFocusId('');
        }
    }, [modalOpen]);

    // Tabs come from the catalog, so a sector the api adds needs no code change
    // here — only a label in sectorCatalog.js.
    const sectorItems = useMemo(() => buildSectorTabs(effectDatabase), [effectDatabase]);

    // Seed sector tab once from onboarding-choice (CompagnyMeta.SelectedSectors).
    // Only runs once after settings load so user clicks aren't overridden.
    // Waits for the catalog too: seeding against an empty tab list would match
    // nothing, fall back to 'all', and mark itself done before the real tabs
    // arrived.
    useEffect(() => {
        if (sectorSeededFromSettings || !settingsLoaded) return;
        const knownTabIds = sectorItems.filter((tab) => tab.id !== 'all').map((tab) => tab.id);
        if (knownTabIds.length === 0) return;
        setSelectedSector(pickDefaultEffectsSector(selectedSectorsFromOnboarding, knownTabIds));
        setSectorSeededFromSettings(true);
    }, [settingsLoaded, selectedSectorsFromOnboarding, sectorSeededFromSettings, sectorItems]);

    // Clear modal error if all required targetgroups are selected
    useEffect(() => {
        if (!modalError) return;

        const stillMissing = selectedEffects.some(
            (e) => e.targetGroupRequired && !e.targetGroup
        );

        if (!stillMissing) {
            setModalError('');
        }
    }, [selectedEffects, modalError]);

    // Determine if a question applies to the chosen targetgroup
    const questionAppliesToTargetGroup = (question, selectedTargetGroup) => {
        const mode = question?.targetGroupsMode || 'all';
        if (mode === 'all') return true;

        const groups = Array.isArray(question?.targetGroups) ? question.targetGroups : [];
        if (!selectedTargetGroup) return false;
        return groups.includes(selectedTargetGroup);
    };

    // Filter categories so empty ones never render (based on selectedSector)
    const filteredCategories = useMemo(() => {
        if (!effectDatabase) return [];

        return effectDatabase
            .map((cat) => {
                const effectsArr = Array.isArray(cat.effects) ? cat.effects : [];

                const filteredEffects =
                    !selectedSector || selectedSector === 'all'
                        ? effectsArr
                        : effectsArr.filter((e) => {
                            const sectors = Array.isArray(e.sectors) ? e.sectors : [];
                            return sectors.includes(selectedSector);
                        });

                return { ...cat, effects: filteredEffects };
            })
            .filter((cat) => (cat.effects || []).length > 0);
    }, [effectDatabase, selectedSector]);

    // ✅ scroll helper
    const scrollToEffectRow = (effectId) => {
        const el = document.getElementById(`msi-effect-row-${effectId}`);
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // pulse highlight (CSS class toggled via state)
        setScrollFocusId(String(effectId));
        window.setTimeout(() => setScrollFocusId(''), 1400);
    };

    // Arrival highlight: a Decca deep-link chip can land here with ?focus=<ID>.
    // Drive the existing pulse; unknown ids no-op (scrollToEffectRow bails).
    const urlFocusId = useFocusHighlight();
    useEffect(() => {
        if (urlFocusId) scrollToEffectRow(urlFocusId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlFocusId]);

    // Save selected effects (Meetstandaard) with targetgroup validation & question filtering
    const saveSelectedEffects = () => {
        const missing = selectedEffects.filter((e) => e.targetGroupRequired && !e.targetGroup);

        if (missing.length > 0) {
            const firstMissing = missing[0];

            setModalError(
                `Kies eerst een doelgroep voor: ${missing
                    .map((m) => m.effect)
                    .slice(0, 3)
                    .join(', ')}${missing.length > 3 ? '...' : ''}`
            );

            // ✅ scroll to the first missing effect row
            scrollToEffectRow(firstMissing.id);
            return;
        }

        const saves = selectedEffects.map((effect) => {
            const fieldId = uuid();
            const effectId = uuid();
            const selectedTargetGroup = effect.targetGroup || '';

            const filteredQuestions = (effect.questions || []).filter((q) =>
                questionAppliesToTargetGroup(q, selectedTargetGroup)
            );

            return db.collection('OutputEffects')
                .add({
                    Effect: effect.effect,
                    Description: effect.description || '',
                    MainEffect: effect.category,
                    Position: effectsLength + 1,
                    ID: effectId,
                    MSIId: effect.id,
                    Compagny: client,
                    CompagnyID: client,
                    Timestamp: timestamp,
                    Standard: 'true',
                    sectors: Array.isArray(effect.sectors) ? effect.sectors : [],
                    TargetGroup: selectedTargetGroup,
                    // Which standaard and which immutable version these
                    // questions were copied from. Empty for the legacy
                    // /database catalogue, which carries no version at all.
                    Standaard: effect.standaard || '',
                    StandaardVersion: effect.standaardVersion || '',
                    MSISlug: effect.slug || '',
                })
                .then((effectRef) => {
                    return db.collection('QuestionnaireFields').doc().set({
                        Compagny: client,
                        CompagnyID: client,
                        ID: fieldId,
                        Timestamp: timestamp,
                        QuestionnaireID: '',
                        Type: 'matrix-one',
                        Question: 'In welke mate ben je het eens met de volgende stellingen?',
                        Explainer: '',
                        ReachStart: 1,
                        ReachStartLable: '',
                        ReachEnd: 5,
                        ReachEndLabel: '',
                        Multiple: [],
                        SectionID: '',
                        MSIId: effect.id,
                        EffectId: [effectId],
                        Indicator: 'true',
                        LiveReportHidden: false,
                        Standard: 'true',
                        Position: 1,
                        IndicatorPosition: 1,
                        TargetGroup: selectedTargetGroup,
                        Standaard: effect.standaard || '',
                        StandaardVersion: effect.standaardVersion || '',
                    }).then(() => effectRef);
                })
                .then((effectRef) => {
                    filteredQuestions.forEach((question, index) => {
                        // Stamped on the ROW because answers point at rows by
                        // RowID — so every response is traceable to the exact
                        // wording it was given under. Payload built by a pure
                        // helper so the stamp is testable without Firestore.
                        db.collection('MatrixQuestionRows').doc().set(
                            buildMatrixRowPayload({
                                effect,
                                question,
                                index,
                                rowId: uuid(),
                                fieldId,
                                client,
                                timestamp,
                                color,
                                targetGroup: selectedTargetGroup,
                            }),
                        );
                    });
                    return effectRef;
                })
                .then((effectRef) => {
                    for (let i = 0; i < 5; i++) {
                        db.collection('MatrixQuestionColumns').doc().set({
                            Title: i + 1,
                            Field: fieldId,
                            Compagny: client,
                            CompagnyID: client,
                            Timestamp: timestamp,
                            Position: i + 1,
                            ID: uuid(),
                            Color: color,
                        });
                    }
                    return { id: effectId, docid: effectRef.id };
                });
        });

        if (typeof onAdded === 'function') {
            Promise.all(saves).then((added) => onAdded(added));
        }

        setModalOpen(false);
    };

    return (
        <>
            <CommonModal
                modalOpen={open}
                setModalOpen={setOpen}
                pageHeader={'Effect toevoegen'}
                hideConfirm={true}
                content={
                    <div className="options-modal-list">
                        <div
                            className="options-modal-item" {...clickableRowProps(() => { setOpen(false); setModalOpen(true); })}
                        >
                            <div className="options-modal-item__icon options-modal-item__icon--meetstandaard">
                                <img src={MeetstandardIcon} alt="" />
                            </div>
                            <div className="options-modal-item__body">
                                <div className="options-modal-item__title-row">
                                    <p className="options-modal-item__title">Effect uit Meetstandaard Social Impact</p>
                                </div>
                                <p className="options-modal-item__description">
                                    Kies uit een gevalideerde set indicatoren van Meetstandaard.nl
                                </p>
                            </div>
                            <ChevronRightIcon className="options-modal-item__chevron" />
                        </div>

                        {parentPortfolioIds.map((portfolioId) => (
                            <PortfolioChooserOption
                                key={portfolioId}
                                portfolioId={portfolioId}
                                portfolioName={portfolioNameById[portfolioId]}
                                itemNoun="Effecten"
                                itemNounLower="effecten"
                                onClick={() => {
                                    setOpen(false);
                                    setSubscribePortfolioId(portfolioId);
                                    setSubscribeModalOpen(true);
                                }}
                            />
                        ))}

                        <div
                            className="options-modal-item" {...clickableRowProps(subscription === 'free' ? null : () => { setOpen(false); onOwnAdd(); })}
                        >
                            <div className="options-modal-item__icon options-modal-item__icon--add">
                                <AddIcon />
                            </div>
                            <div className="options-modal-item__body">
                                <div className="options-modal-item__title-row">
                                    <p className="options-modal-item__title">Eigen effect toevoegen</p>
                                    <PremiumIcon />
                                </div>
                                <p className="options-modal-item__description">
                                    Beschrijf zelf een effect — vrij in te vullen, geen sjabloon.
                                </p>
                            </div>
                            <ChevronRightIcon className="options-modal-item__chevron" />
                        </div>
                    </div>
                }
            />

            {subscribeModalOpen && (
                <SubscribePortfolioEffectsModal
                    modalOpen={subscribeModalOpen}
                    setModalOpen={setSubscribeModalOpen}
                    preselectedPortfolioId={subscribePortfolioId}
                    onSubscribed={onAdded}
                />
            )}

            <CommonModal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                pageHeader="Meetstandaard Social Impact"
                confirm={saveSelectedEffects}
                content={
                    <>
                        {/* Sector tabs/grid */}
                        <div className="msi-sector-grid-container">
                            <SelectableGrid
                                items={sectorItems}
                                getId={(item) => item.id}
                                getLabel={(item) => item.label}
                                selectedId={selectedSector}
                                onClick={(id) => setSelectedSector(id)}
                            />
                        </div>

                        <div>
                            {effectDatabase ? (
                                filteredCategories.map((category, index) => (
                                    <EffectDatabaseItemsList
                                        key={category.id || index}
                                        category={category}
                                        index={index}
                                        setSelectedEffects={setSelectedEffects}
                                        selectedSector={selectedSector}
                                        selectedEffects={selectedEffects}
                                        scrollFocusId={scrollFocusId}   // ✅ pass focus id for pulse class
                                    />
                                ))
                            ) : (
                                <img src={spinner} alt="" />
                            )}
                        </div>

                        {modalError ? <div className="modal-error">{modalError}</div> : null}
                    </>
                }
            />
        </>
    );
};

export default AddEffectFlow;
